// A building's structural history, inside Building readiness's structural area: where the
// assessment stands, the version it rests on and its sign-off (which date the Amazon
// overview's Structural assessment and Structural sign-off), each report version with its
// Roof viable figure and works, the feedback each party owes, and every exchange, latest
// first. The state, tiles and feedback come from the backend
// (structural_status.card_payload). See docs/Y2026-Q4/design-structural-history.md.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useState } from "react";

import type {
  StructuralHistory,
  StructuralReport,
  StructuralVersionReport,
  StructuralWork,
} from "../api/hooks/useProjectAgentStatus";
import { useStructuralRelyOn } from "../api/hooks/useStructuralRelyOn";
import { formatDay } from "../utils/attributeFormat";
import styles from "./EmailHistory.module.css";
import {
  BriefLine,
  ExchangesSection,
  FeedbackSection,
  FeedbackTile,
  StateLine,
  Tile,
  type Cite,
} from "./ReadinessHistoryParts";


const ROLE_LABEL: Record<StructuralReport["role"], string> = {
  ours: "Our engineer",
  client: "Client's engineer",
  third_party: "Third party",
  mounting: "Mounting company",
};

const VERDICT_LABEL: Record<StructuralReport["verdict"], string> = {
  adequate: "Adequate",
  adequate_with_works: "Adequate after works",
  not_adequate: "Not adequate",
  inconclusive: "Inconclusive",
  not_stated: "Not stated",
};

const TIMING_LABEL: Record<StructuralWork["timing"], string> = {
  before_pv: "Before PV",
  after_pv: "After PV",
  not_stated: "",
};

const STATUS_LABEL: Record<StructuralWork["status"], string> = {
  required: "Required",
  agreed: "Agreed",
  done: "Done",
};

const share = (s: { min_pct: number; max_pct: number }) =>
  s.min_pct === s.max_pct ? `${s.min_pct}%` : `${s.min_pct}-${s.max_pct}%`;

const viableText = (viable: NonNullable<StructuralReport["roof_viable"]>) =>
  share(viable) + (viable.after_works ? " after works" : "");

const kwpText = (n: number) => `${n.toLocaleString("en-GB")} kWp`;

const viableKwp = ({ of, min, max }: { of: number; min: number; max: number }) => (
  <>
    of {kwpText(of)} max-out design
    <br />={" "}
    {min === max
      ? kwpText(min)
      : `${min.toLocaleString("en-GB")}-${kwpText(max)}`}{" "}
    viable
  </>
);

/** The operator's choice of the version we rely on, with an optional note. */
const RelyOn = ({
  projectId,
  version,
}: {
  projectId: string;
  version: StructuralVersionReport;
}) => {
  const { user } = useKindeAuth();
  const relyOn = useStructuralRelyOn();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  if (!open) {
    return (
      <button
        type="button"
        className={styles.relyButton}
        onClick={() => setOpen(true)}
      >
        Rely on this version
      </button>
    );
  }
  return (
    <div className={styles.relyForm}>
      <label className={styles.note}>
        Why (optional, kept in the history)
        <textarea
          className={styles.relyNote}
          value={note}
          maxLength={240}
          rows={2}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className={styles.relyButtons}>
        <button
          type="button"
          className={styles.relyButton}
          disabled={relyOn.isLoading}
          onClick={() =>
            relyOn.mutate(
              {
                project_id: projectId,
                entry_id: version.entry_id,
                note,
                actor_email: user?.email ?? "",
              },
              { onSuccess: () => setOpen(false) },
            )
          }
        >
          {relyOn.isLoading ? "Saving..." : `Rely on ${version.version}`}
        </button>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
      {relyOn.error && (
        <p className={styles.relyError} role="alert">
          {relyOn.error.message}
        </p>
      )}
    </div>
  );
};

const Works = ({ works, cite }: { works: StructuralWork[]; cite: Cite }) =>
  works.length === 0 ? (
    <>None</>
  ) : (
    <ul className={styles.points}>
      {works.map((w) => (
        <li key={w.text}>
          {w.text}
          {TIMING_LABEL[w.timing] && (
            <span className={`${styles.tag} ${styles.works}`}>
              {" "}
              {TIMING_LABEL[w.timing]}
            </span>
          )}
          <span className={styles.note}>
            {STATUS_LABEL[w.status]}
            {w.status_entry && (
              <>
                : <BriefLine brief={w.status_entry} cite={cite} />
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  );

export const StructuralHistoryCard = ({
  history,
  cite,
  projectId,
  editable,
}: {
  history: StructuralHistory;
  cite: Cite;
  projectId: string;
  /** Today's view: an operator can choose the version we rely on. False on an
   * earlier briefing version, which shows the history as it stood. */
  editable: boolean;
}) => {
  const { signoff, decision, before_pv: beforePv } = history;
  const signoffNote = signoff.newer
    ? `${signoff.newer.version} issued since; not accepted yet`
    : !signoff.accepted && history.points_open === 0 && signoff.passed_on
      ? "No feedback open; waiting for acceptance"
      : undefined;
  return (
    <>
      <StateLine state={history.state} />
      <div className={`${styles.stages} ${styles.tiles}`}>
        <Tile
          label="Relied on"
          main={history.relied_on?.version}
          empty={
            history.disputed.length
              ? "None: engineers disagree"
              : "No complete version yet"
          }
          sub={
            history.relied_on && (
              <>
                {decision
                  ? `${decision.kind_label} by ${decision.sender} on ${formatDay(decision.date)}`
                  : `Issued ${formatDay(history.relied_on.date)}, the latest complete`}
                , {cite(history.relied_on.email)}
              </>
            )
          }
        />
        <Tile
          label="Roof viable"
          main={history.roof_viable?.text}
          empty={
            history.relied_on ? "Not stated in the report" : "No version relied on"
          }
          sub={history.roof_viable?.kwp && viableKwp(history.roof_viable.kwp)}
        />
        <FeedbackTile history={history} />
        <Tile
          label="Before PV"
          main={
            beforePv
              ? beforePv.open.length
                ? beforePv.open.map((w) => w.text).join(" + ")
                : beforePv.total
                  ? "None open"
                  : "No works required"
              : ""
          }
          empty="No version relied on"
          sub={
            beforePv &&
            [
              ...beforePv.open.map((w) => `${w.text}: ${STATUS_LABEL[w.status]}`),
              beforePv.done ? `${beforePv.done} done` : "",
              beforePv.untimed
                ? `${beforePv.untimed} work${beforePv.untimed === 1 ? "" : "s"} the report gives no timing for`
                : "",
            ]
              .filter(Boolean)
              .join(" · ")
          }
        />
        <Tile
          label="Signed off"
          main={signoff.accepted && signoff.of?.version}
          empty={
            signoff.passed_on
              ? `Sent for review ${formatDay(signoff.passed_on.date)}`
              : "Not sent for review"
          }
          sub={
            signoff.accepted ? <BriefLine brief={signoff.accepted} cite={cite} /> : signoffNote
          }
        />
      </div>

      {history.disputed.length > 0 && (
        <div className={styles.dispute} role="alert">
          <strong className={styles.disputeHead}>Engineers disagree</strong>
          <p className={styles.disputeText}>
            Nothing relies on either report until one is chosen, so the
            assessment and its sign-off wait. The choice is kept in the history
            as yours.
          </p>
          <ul className={styles.disputeList}>
            {history.disputed.map((v) => (
              <li key={v.entry_id}>
                <strong>{v.version}</strong>, {ROLE_LABEL[v.report.role]},
                issued {formatDay(v.date)}: {VERDICT_LABEL[v.report.verdict]}
                {v.report.roof_viable &&
                  `, roof viable ${viableText(v.report.roof_viable)}`}
                . {cite(v.email)}
                {editable && <RelyOn projectId={projectId} version={v} />}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionLabel}>Report versions</h4>
        {history.versions.length === 0 ? (
          <p className={styles.empty}>No report version logged yet.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Issued</th>
                  <th>Version</th>
                  <th>Verdict</th>
                  <th>Roof viable</th>
                  <th>Works it needs</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {history.versions.map((v) => (
                  <tr key={v.entry_id}>
                    <td
                      className={`${styles.date} ${v.relied_on ? styles.first : ""}`}
                    >
                      {formatDay(v.date)}
                    </td>
                    <td>
                      <strong>
                        {v.report.firm} {v.report.revision}
                      </strong>
                      {v.report.status === "draft" && (
                        <span className={`${styles.tag} ${styles.other}`}>
                          {" "}
                          Draft
                        </span>
                      )}
                      <span className={styles.note}>
                        {ROLE_LABEL[v.report.role]}
                        {v.report.reference && `, ${v.report.reference}`}
                      </span>
                      {v.relied_on && (
                        <span className={styles.note}>Relied on</span>
                      )}
                      {v.accepted && (
                        <span className={styles.note}>
                          Accepted {formatDay(v.accepted.date)}
                        </span>
                      )}
                      {v.report.document && (
                        <span className={styles.note}>
                          {cite(v.report.document)}
                        </span>
                      )}
                      {editable &&
                        !v.relied_on &&
                        v.report.status === "complete" &&
                        // Mounting calculations check the system, not the roof.
                        v.report.role !== "mounting" &&
                        // A choice of an earlier revision would rely on the latest.
                        !v.superseded &&
                        history.disputed.length === 0 && (
                          <RelyOn projectId={projectId} version={v} />
                        )}
                    </td>
                    <td>
                      {VERDICT_LABEL[v.report.verdict]}
                      {v.report.existing_fails && (
                        <span className={styles.note}>
                          Existing structure fails without PV
                        </span>
                      )}
                      {v.report.client_spec && (
                        <span className={styles.note}>
                          Against {v.report.client_spec}
                        </span>
                      )}
                      {v.report.mounting_design && (
                        <span className={styles.note}>
                          Mounting: {v.report.mounting_design}
                        </span>
                      )}
                    </td>
                    <td>
                      {v.report.roof_viable ? (
                        <>
                          <strong>{viableText(v.report.roof_viable)}</strong>
                          {v.report.roof_viable_now && (
                            <span className={styles.note}>
                              As the roof stands:{" "}
                              {share(v.report.roof_viable_now)}.{" "}
                              {v.report.roof_viable_now.reason}
                            </span>
                          )}
                          <span className={styles.note}>
                            {v.report.roof_viable.reason}
                          </span>
                        </>
                      ) : (
                        <span className={styles.notRecorded}>
                          Not stated in the report
                        </span>
                      )}
                    </td>
                    <td className={styles.summary}>
                      <Works works={v.report.works} cite={cite} />
                    </td>
                    <td>{cite(v.email)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <FeedbackSection history={history} cite={cite} />
      <ExchangesSection
        entries={history.entries}
        cite={cite}
        empty="Building readiness has not logged any structural exchange yet."
      />
    </>
  );
};
